import { ActionStatus } from '../../constants/api';
import CreateAction from '../../utils/actions';

// Existing actions
export const UPDATE_ERROR_MESSAGE = CreateAction<string>('UPDATE_ERROR_MESSAGE');
export const TOGGLE_ERROR_DIALOG = CreateAction<boolean>('TOGGLE_ERROR_DIALOG');

// Updated GET_NEWS action - no payload needed for Reddit API
export const GET_NEWS = CreateAction<void>('GET_NEWS');

// New actions for better news management
export const CLEAR_NEWS = CreateAction<void>('CLEAR_NEWS');
export const SET_CACHED_NEWS = CreateAction<Story[]>('SET_CACHED_NEWS');
export const SET_NEWS_ERROR = CreateAction<string>('SET_NEWS_ERROR');

// Story interface to match your component
export interface Story {
  title: string;
  link: string;
  pubDate: string;
  creator?: string;
  image?: string;
  author?: string;
}

// Updated HomeState interface
export interface HomeState {
  currentTab: string;
  news: Story[]; // Changed from any to Story[]
  NEWS_STATUS: ActionStatus;
  error: string | null; // Changed from any to string | null for better typing
  lastFetched: number | null; // Track when news was last fetched
  cached: boolean; // Track if current news is from cache
  loading: boolean; // Add loading state for better UX
}

// Action payload types for better type safety
export interface GetNewsPayload {
  forceRefresh?: boolean; // Optional flag to force refresh even if cache is valid
}

// Error payload type
export interface NewsErrorPayload {
  message: string;
  code?: string;
  timestamp: number;
}

// Optional: Create specific action for setting news with metadata
export const SET_NEWS_WITH_METADATA = CreateAction<{
  stories: Story[];
  cached: boolean;
  timestamp: number;
}>('SET_NEWS_WITH_METADATA');

// Optional: Create action for partial news update (useful for infinite scroll)
export const APPEND_NEWS = CreateAction<Story[]>('APPEND_NEWS');

// Cache management actions
export const CLEAR_NEWS_CACHE = CreateAction<void>('CLEAR_NEWS_CACHE');
export const VALIDATE_NEWS_CACHE = CreateAction<void>('VALIDATE_NEWS_CACHE');

// Export action types for saga use
export type NewsActionTypes =
  | typeof GET_NEWS
  | typeof CLEAR_NEWS
  | typeof SET_CACHED_NEWS
  | typeof SET_NEWS_ERROR
  | typeof SET_NEWS_WITH_METADATA
  | typeof APPEND_NEWS
  | typeof CLEAR_NEWS_CACHE
  | typeof VALIDATE_NEWS_CACHE;

// Helper type for action creators
export type NewsActionCreators = {
  getNews: typeof GET_NEWS;
  clearNews: typeof CLEAR_NEWS;
  setCachedNews: typeof SET_CACHED_NEWS;
  setNewsError: typeof SET_NEWS_ERROR;
  setNewsWithMetadata: typeof SET_NEWS_WITH_METADATA;
  appendNews: typeof APPEND_NEWS;
  clearNewsCache: typeof CLEAR_NEWS_CACHE;
  validateNewsCache: typeof VALIDATE_NEWS_CACHE;
};

// Constants for cache management
export const NEWS_CACHE_CONFIG = {
  CACHE_DURATION: 60 * 60 * 1000, // 1 hour in milliseconds
  MAX_STORIES: 10,
  STORAGE_KEY: 'cachedStories',
  TIMESTAMP_KEY: 'storiesCacheTime',
} as const;

// Fallback stories constant
export const FALLBACK_STORIES: Story[] = [
  {
    title: "Technology Trends Shaping Our Future",
    link: "#",
    pubDate: new Date().toISOString(),
    author: "Tech Team",
    image: "https://placehold.co/600x400/png",
  },
  {
    title: "AI Advancements in 2025",
    link: "#",
    pubDate: new Date().toISOString(),
    author: "AI Research Team",
    image: "https://placehold.co/600x400/png",
  },
  {
    title: "The Future of Mobile Technology",
    link: "#",
    pubDate: new Date().toISOString(),
    author: "Mobile Tech Review",
    image: "https://placehold.co/600x400/png",
  },
];

// Type guards for better type safety
export const isStoryArray = (data: any): data is Story[] => {
  return Array.isArray(data) && data.every(item => 
    typeof item === 'object' && 
    typeof item.title === 'string' && 
    typeof item.link === 'string' && 
    typeof item.pubDate === 'string'
  );
};

export const isValidStory = (story: any): story is Story => {
  return (
    typeof story === 'object' &&
    typeof story.title === 'string' &&
    typeof story.link === 'string' &&
    typeof story.pubDate === 'string' &&
    (story.author === undefined || typeof story.author === 'string') &&
    (story.image === undefined || typeof story.image === 'string') &&
    (story.creator === undefined || typeof story.creator === 'string')
  );
};